import React, { useEffect, useRef, useState } from "react";
import { Box, Flex, Icon, css } from "@storyofams/react-ui";
import Image from "next/image";
import "react-photo-view/dist/react-photo-view.css";
import ReactPlayer from "react-player";
import SwiperCore from "swiper";
import "swiper/css";
import { Swiper, SwiperSlide } from "swiper/react";
import { PlayButton, PlayVideo } from "~components/common/Icons";
import { GallerySlider } from "~components/common/Slider/GallerySlider";
import { FixedRatio } from "../../../common/FixedRatio";
import { StockLabel } from "../../components";
import { Modal } from "./Modal";

export type SliderProps = {
  inStock?: boolean;
  hideStockLabel?: boolean;
  variant: any;
  product: any;
  videoEmbed?: string;
  videoEmbedPreview?: string;
  embedPreview?: any;
};

const blockedIds = [
  "gid://shopify/Product/6724635131959",
  "gid://shopify/Product/8762003063125",
  "gid://shopify/Product/8819542557013",
];

export const ImageSlider = ({
  inStock,
  product,
  hideStockLabel,
  variant,
  videoEmbed,
  videoEmbedPreview,
  embedPreview,
}: SliderProps) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const swiperRef = useRef() as any;
  const isFirstTime = useRef(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const images = product?.images?.edges
    .filter(({ node }) => node?.image?.altText !== "sample")
    .map(({ node }) => {
      switch (node.mediaContentType) {
        case "IMAGE":
          return { src: node.image.url, type: "image" };
        case "VIDEO": {
          if (node.sources && node.sources.length > 1)
            return {
              src: node.sources.find((item) => item.url.includes("720"))?.url,
              type: "video",
            };
          return {
            src: node.sources[0].url,
            type: "video",
          };
        }

        default:
          return {
            src: node.originUrl,
            type: "video",
            preview: node?.previewImage?.url,
          };
      }
    });
  const smallImages = product?.smallImages?.edges
    .filter(({ node }) => node?.image?.altText !== "sample")
    .map(({ node }) => {
      if (node.mediaContentType === "IMAGE") {
        return { src: node.image.url, type: "image" };
      }
      if (node.mediaContentType === "VIDEO") {
        if (node.sources && node.sources.length > 1)
          return {
            src: node.sources.find((item) => item.url.includes("720"))?.url,
            type: "video",
          };
        return { src: node.sources[0].url, type: "video" };
      }
      return { src: node?.previewImage?.url, type: "image" };
    });
  useEffect(() => {
    if (variant) {
      if (blockedIds[0] === product.id) {
        setActiveIndex(0);
        swiperRef.current?.swiper.slideTo(0);
      } else if (!isFirstTime.current && product.id === blockedIds[1]) {
        isFirstTime.current = true;
        setActiveIndex(0);
        swiperRef.current?.swiper.slideTo(0);
      } else if (!isFirstTime.current && product.id === blockedIds[2]) {
        isFirstTime.current = true;
        setActiveIndex(0);
        swiperRef.current?.swiper.slideTo(0);
      } else {
        const variantImg = variant?.image?.originalSrc?.includes(".png")
          ? variant?.image?.originalSrc.replace(".png", "_1000x1000.png")
          : variant?.image?.originalSrc.replace(".png", "_1000x1000.jpg");
        let index = images.findIndex((img) => img.src === variantImg);
        if (index !== -1 && activeIndex !== index) {
          setActiveIndex(index);
          swiperRef.current?.swiper.slideTo(index);
        }
      }
    }
  }, [variant]);
  const content = videoEmbed
    ? [{ type: "embed", src: videoEmbed, preview: null }, ...images]
    : images;
  const smallContent = videoEmbed
    ? [
        { type: "embed", src: embedPreview?.filename, preview: null },
        ...smallImages,
      ]
    : smallImages;
  const sliderContent = videoEmbed
    ? [{ type: "embed", src: videoEmbedPreview, preview: null }, ...images]
    : images;
  const close = () => {
    setIsOpen(false);
  };
  return (
    <>
      <Box>
        <Box
          width="100%"
          position="relative"
          m="0 auto"
          css={css({
            width: ["100%", "100%", "100%"],
            ".swiper-button-next, .swiper-button-prev": {
              "&:after": {
                content: "unset",
              },
              top: "unset",
              bottom: [1, "50%"],
            },
            ".swiper-container": {
              width: "100%",
              borderRadius: "8px",
            },
            ".product-slider": {
              pb: [1, 2],

              ".swiper-slide": {
                width: "100%!important",
              },
            },
          })}
        >
          <Flex
            zIndex={5}
            bottom={[3, 7]}
            width={"100%"}
            justifyContent={"center"}
            position={"absolute"}
          >
            {content.map(({ src }, i) => (
              <Box
                borderRadius={"full"}
                key={src}
                mr={1}
                height={"10px"}
                width={"10px"}
                bg={activeIndex === i ? "white" : "grey400"}
              ></Box>
            ))}
          </Flex>
          <Box>
            {content?.length >= 1 && (
              <Swiper
                onInit={(core: SwiperCore) => {
                  swiperRef.current = core.el;
                }}
                slidesPerView={1}
                spaceBetween={24}
                mousewheel={false}
                className="product-slider"
                lazyPreloadPrevNext={1}
                onSlideChange={(e) => {
                  setActiveIndex(e.activeIndex);
                  setIsPlaying(false);
                }}
              >
                {content?.map(({ type, src, preview }, i) => (
                  <SwiperSlide
                    onClick={() => {
                      setIsOpen(true);
                    }}
                    key={src + i}
                  >
                    <FixedRatio
                      ratio={[
                        [260, 260],
                        [640, 640],
                      ]}
                      borderRadius={"8px"}
                      overflow={"hidden"}
                      css={css({
                        video: {
                          borderRadius: "8px",
                        },
                        img: {
                          borderRadius: "8px",
                        },
                      })}
                    >
                      {type === "image" ? (
                        <Image
                          src={src}
                          layout="fill"
                          objectFit="cover"
                          objectPosition="center"
                          priority={i === activeIndex}
                          // blurDataURL={images?.length && images[i]?.src}
                          // placeholder={images?.length ? "blur" : "empty"}
                        />
                      ) : type === "embed" ? (
                        <Box onClick={() => setActiveIndex(0)}>
                          <div dangerouslySetInnerHTML={{ __html: src }}></div>
                        </Box>
                      ) : src.includes("youtu") ? (
                        <>
                          <Box
                            width={"100%"}
                            height={"100%"}
                            display={isPlaying ? "block" : "none"}
                          >
                            <ReactPlayer
                              loop={true}
                              playsinline
                              playing={isPlaying}
                              width={"100%"}
                              onPause={() => setIsPlaying(false)}
                              style={{
                                backgroundImage: "none",
                                objectFit: "cover",
                              }}
                              on
                              height={"100%"}
                              stopOnUnmount={true}
                              url={src}
                            />
                          </Box>
                          <Box display={!isPlaying ? "block" : "none"}>
                            <Image
                              layout="fill"
                              objectFit="cover"
                              src={preview}
                            />
                            <Box
                              width={"80px"}
                              top={0}
                              left={0}
                              right={0}
                              pt="12px"
                              bottom={0}
                              margin={"auto"}
                              pl="27px"
                              borderRadius={"4px"}
                              height={"55px"}
                              bg="white"
                              position={"absolute"}
                              onClick={() => setIsPlaying(true)}
                              cursor={"pointer"}
                            >
                              <Icon icon={PlayButton} fontSize={"30px"} />
                            </Box>
                          </Box>
                        </>
                      ) : (
                        <ReactPlayer
                          loop={true}
                          width={"100%"}
                          style={{
                            backgroundImage: "none",
                            objectFit: "cover",
                          }}
                          muted
                          on
                          playing
                          height={"100%"}
                          stopOnUnmount={true}
                          url={src}
                          playsinline
                        />
                      )}
                    </FixedRatio>
                  </SwiperSlide>
                ))}
              </Swiper>
            )}
          </Box>
          {!hideStockLabel && <StockLabel inStock={inStock} />}
        </Box>
        <Box
          width={"100%"}
          overflow={"clip"}
          display={"flow-root"}
          css={{ "overflow-clip-margin": "content-box 10px;" }}
        >
          <GallerySlider slidesPerView={4}>
            {smallContent.map(({ type, src }, i) => (
              <FixedRatio
                ratio={[[1, 1]]}
                key={src}
                cursor={"pointer"}
                border={i === activeIndex ? "1px solid black" : "none"}
                borderRadius={"8px"}
                width={"100%"}
                position={"relative"}
                css={css({
                  video: {
                    borderRadius: "8px",
                    objectFit: "cover",
                  },
                  img: {
                    borderRadius: "8px",
                  },
                })}
              >
                {type === "image" ? (
                  <Image
                    onClick={() => {
                      swiperRef.current?.swiper.slideTo(i);
                    }}
                    src={src}
                    objectFit="cover"
                    layout="fill"
                  />
                ) : type === "embed" ? (
                  <Box
                    borderRadius={"8px"}
                    width={"100%"}
                    height={"100%"}
                    onClick={() => {
                      swiperRef.current?.swiper.slideTo(i);
                    }}
                  >
                    <Image src={src} objectFit="cover" layout="fill" />

                    <Icon
                      width={"100%"}
                      mx="auto"
                      left={0}
                      right={0}
                      top={["30%", "34%"]}
                      position={"absolute"}
                      icon={PlayVideo}
                      ml={"3px"}
                      color={"white"}
                      fontSize={["44px", "57px"]}
                    />
                  </Box>
                ) : (
                  <Box
                    borderRadius={"8px"}
                    width={"100%"}
                    height={"100%"}
                    onClick={() => {
                      swiperRef.current?.swiper.slideTo(i);
                    }}
                  >
                    <ReactPlayer
                      url={src}
                      position={"relative"}
                      playing={false}
                      width={"100%"}
                      height={"100%"}
                    />

                    <Icon
                      width={"100%"}
                      mx="auto"
                      left={0}
                      right={0}
                      top={["30%", "34%"]}
                      position={"absolute"}
                      icon={PlayVideo}
                      ml={"3px"}
                      color={"white"}
                      fontSize={["44px", "52px"]}
                    />
                  </Box>
                )}
              </FixedRatio>
            ))}
          </GallerySlider>
        </Box>
      </Box>
      <Modal
        sliderContent={sliderContent}
        isOpen={isOpen}
        activeIndex={activeIndex}
        close={close}
      />
    </>
  );
};
